<template>
    <v-dialog v-model="show_dialog" :persistent="true" max-width="1264px" scrollable>
        <v-card class="custom_card">

            <v-card-title>
                <span class="headline">{{header}}</span>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text id="manage_sale_page">
                <div v-if="loading_data" class="pb-4">
                    <v-container fluid class="incident_loading"
                                 :class="[loading_data ? 'justify-center fill-height' : '']">
                        <v-progress-circular :size="50" indeterminate color="primary"></v-progress-circular>
                    </v-container>
                </div>
                <div v-else class="pb-4">
                    <v-row dense>
                        <v-col cols="12">
                            <div class="title mb-4">{{$t('sales.data')}}</div>
                        </v-col>
                        <v-col cols="12" sm="6" md="6" lg="3">
                            <v-menu v-model="sale_date_menu" :close-on-content-click="false"
                                    max-width="290">
                                <template v-slot:activator="{ on }">
                                    <v-text-field name="date" type="text" class="custom_field"
                                                  :value="$Helper.methods.formatDate(item.sale_date)"
                                                  :label="$t('sales.date')" outlined readonly
                                                  :disabled="isFieldsDisabled" v-on="on"
                                                  @blur="$v.item.sale_date.$touch()"
                                                  @input="dateFieldCleared('sale_date')">
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="item.sale_date" no-title @change="sale_date_menu = false">
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" sm="6" md="6" lg="3">
                            <v-select name="client_id" class="custom_field" v-model="item.client_id" :items="clients"
                                      :label="$t('clients.singular')" item-text="client_name" item-value="id" outlined
                                      clearable :disabled="isFieldsDisabled" @input="$v.item.client_id.$touch()"
                                      @blur="$v.item.client_id.$touch()" :error-messages="fieldErrors('client_id')">
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="6" md="6" lg="3">
                            <v-text-field name="price" type="text" class="custom_field" v-model="item.price"
                                          :label="$t('general.price')" outlined clearable :disabled="isFieldsDisabled"
                                          @input="$v.item.price.$touch()" @blur="$v.item.price.$touch()"
                                          :error-messages="fieldErrors('price')">
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="6" lg="3">
                            <v-select name="order_status_id" class="custom_field" v-model="item.order_status_id"
                                      :items="order_statuses" :label="$t('general.order_status')" item-text="name"
                                      item-value="id" outlined clearable :disabled="isFieldsDisabled"
                                      @input="$v.item.order_status_id.$touch()" @blur="$v.item.order_status_id.$touch()"
                                      :error-messages="fieldErrors('order_status_id')">
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field name="description" class="custom_field" v-model="item.description"
                                          :label="$t('general.description')" outlined clearable no-resize
                                          :disabled="isFieldsDisabled" @input="$v.item.description.$touch()"
                                          @blur="$v.item.description.$touch()"
                                          :error-messages="fieldErrors('description')">
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" class="mb-4">
                            <SalePaymentsList :list_items="payments" :sale_id="this.item_id"
                                              :view_mode="action === 'view'" @items-changed="itemsChanged">
                            </SalePaymentsList>
                        </v-col>
                    </v-row>
                    <v-row dense justify="end">
                        <v-col cols="12" sm="6" md="6" lg="3">
                            <v-text-field name="remaining_balance" type="text" class="custom_field"
                                          v-model="item.remaining_balance" :label="$t('sales.remaining_balance')"
                                          outlined :disabled="isFieldsDisabled" readonly
                                          @input="$v.item.remaining_balance.$touch()"
                                          @blur="$v.item.remaining_balance.$touch()"
                                          :error-messages="fieldErrors('remaining_balance')">
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row dense v-if="item.remaining_balance > 0">
                        <v-col cols="12" id="paid_sale">
                            <v-checkbox name="paid" v-model="paid" :label="$t('general.paid')" color="primary"
                                        class="custom_checkbox" @click="settleSaleRemainingBalance">
                            </v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <v-textarea name="comments" class="custom_field" v-model="item.comments"
                                        :label="$t('general.comments')" outlined clearable no-resize
                                        :disabled="isFieldsDisabled" rows="2">
                            </v-textarea>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>
            <v-divider></v-divider>

            <v-card-actions class="elevation-1">
                <v-spacer></v-spacer>
                <v-btn color="primary" class="close_button" dark @click="closeDialog">
                    {{$t('general.close')}}
                </v-btn>
                <v-btn v-if="action === 'view' && $Helper.methods.canUserDoAction(canUpdateItem, isSuperAdmin, item)"
                       color="primary" class="update_button" dark @click="updateItem">
                    {{$t('sales.update')}}
                </v-btn>
                <v-btn v-if="action !== 'view'" class="primary submit_button" dark :loading="is_loading"
                       @click="submitDialog()">
                    {{$t('general.save')}}
                </v-btn>
            </v-card-actions>

        </v-card>
    </v-dialog>
</template>

<script>
    import {validationMixin} from 'vuelidate'
    import {required, numeric} from 'vuelidate/lib/validators'
    import SalePaymentsList from '../components/SalePaymentsList.vue'

    export default {
        props: {
            show: {type: Boolean, default: false},
            item_header: {type: String},
            item_action: {type: String},
            item_id: {type: Number}
        },
        components: {SalePaymentsList},
        mixins: [validationMixin],
        validations() {
            return {
                item: {
                    sale_date: {required},
                    client_id: {required},
                    description: {required},
                    price: {required, numeric},
                    remaining_balance: {required},
                    order_status_id: {required}
                }
            }
        },
        data: self => ({
            show_dialog: self.show,
            action: self.item_action,
            header: self.item_header,
            url: 'sales',
            loading_data: false,
            is_loading: false,
            offsetTop: 0,

            // actions permissions
            canUpdateItem: false,

            // look up data
            clients: [],
            order_statuses: [],

            // data
            sale_date_menu: false,
            payments: [],

            // item
            item: {
                sale_date: null,
                client_id: null,
                description: null,
                price: null,
                remaining_balance: 0,
                order_status_id: null,
                comments: null,
                created_at: null
            },

            paid: false
        }),
        computed: {
            isFieldsDisabled() {
                return this.action === 'view';
            },
            price() {
                return this.item.price;
            },
            isSuperAdmin() {
                return this.$store.state.user_details.roles.map(obj => obj.name).indexOf('superadmin') > -1;
            }
        },
        async mounted() {
            this.canUpdateItem = await this.$store.dispatch('hasPermission', 'update_expense');

            // get clients
            let client_list = await this.$store.dispatch('apiRequest', {
                method: 'get',
                url: 'clients/list'
            });
            this.clients = this.$Helper.methods.sortArrayOfObjects({array: client_list.data, property: 'client_name'});

            // get order statuses
            let order_statuses = await this.$store.dispatch('apiRequest', {
                method: 'get', url: 'settings/order_statuses'
            });
            this.order_statuses = this.$Helper.methods.sortArrayOfObjects({
                array: order_statuses.data, property: 'name'
            });
        },
        watch: {
            async show(val) {
                this.show_dialog = val;

                if (val) {

                    this.action = this.item_action;
                    this.item.sale_date = new Date().toISOString().substr(0, 10);

                    // create
                    if (this.action === 'create') {

                        // set default payment type
                        this.$nextTick().then(() => {
                            this.item.order_status_id = 1;
                        });

                        // view, update
                    } else {

                        let response = await this.$store.dispatch('apiRequest', {
                            method: 'get', url: `${this.url}/${this.item_id}`,
                        });

                        // successful response
                        if (!response.response) {

                            // set item data
                            this.$nextTick().then(() => {
                                for (let prop in this.item) {
                                    this.item[prop] = response.data[prop];
                                }

                                // set data in payments list component
                                this.payments = response.data.payments;

                                // call in order to calculate sale's remaining balance
                                this.itemsChanged(response.data.payments);
                            });

                            // error response
                        } else {
                            this.clearDialog();
                            this.$emit('close-dialog');
                        }
                    }
                }
            },
            item_action(val) {
                this.action = val;
            },
            item_header(val) {
                this.header = val;
            },
            price(val) {
                if (val) {
                    this.calculateRemainingBalance();
                }
            }
        },
        methods: {
            // form field errors
            fieldErrors(prop) {
                const errors = [];
                if (!this.$v.item[prop].$dirty) return errors;

                if (['price'].indexOf(prop) > -1) {
                    !this.$v.item[prop].numeric && errors.push(this.$t('validation.numeric'));
                }

                !this.$v.item[prop].required && errors.push(this.$t('validation.required'));
                return errors;
            },
            // clear dialog
            clearDialog() {
                this.$v.item.$reset();
                this.is_loading = false;

                for (let prop in this.item) {
                    this.item[prop] = prop === 'remaining_balance' ? 0 : null;
                }

                this.payments = [];
            },
            // trigger close dialog event in parent component
            async closeDialog() {
                this.clearDialog();
                this.$emit('close-dialog');
            },
            // trigger submit dialog event in parent component
            async submitDialog() {
                this.$v.item.$touch();

                // item is invalid
                if (!this.$v.item.$invalid) {

                    this.is_loading = true;

                    let response = await this.$store.dispatch('apiRequest', {
                        method: 'post',
                        url: this.action !== 'create' ? `${this.url}/${this.item_id}` : this.url,
                        data: this.item
                    });

                    this.is_loading = false;

                    // successful response
                    if (!response.response) {
                        this.$emit('submit-dialog');
                        this.clearDialog();
                    }
                }
            },
            // change view to update
            updateItem() {
                this.action = 'update';
                this.header = this.$t('sales.update');
            },
            // submit event from sale payment dialog
            itemsChanged(items) {
                this.item.payments = items;

                if (this.item.price) {
                    this.calculateRemainingBalance();
                }
            },
            // calculate the remaining balance
            calculateRemainingBalance() {

                // get all payments sum amount
                let payments_total_amount = this.item.payments
                    ? this.item.payments.map(obj => parseInt(obj.amount)).reduce((a, b) => a + b, 0) : 0;

                // calculate the remaining balance
                this.item.remaining_balance = parseInt(this.item.price) - payments_total_amount;

                // update max value that can have a payment
                this.$store.commit('mutateRemainingBalance', this.item.remaining_balance);

                // update sale price value
                this.$store.commit('mutatePaymentPrice', this.item.price > 0 ? this.item.price : 0);

                // update if user can create new payment
                this.$store.commit('mutateCanCreatePayment', (!payments_total_amount > 0) ? true
                    : payments_total_amount !== parseInt(this.item.price));

                // set order status based on remaining balance value
                this.item.order_status_id = this.item.remaining_balance > 0 ? 2 : 1;
            },
            // add sale to settle sale
            settleSaleRemainingBalance() {

                // empty payments array to not insert duplicates
                this.payments = [];

                // set new payment object
                let payment = {
                    amount: this.item.remaining_balance,
                    date: new Date().toISOString().substr(0, 10),
                    id: null,
                    payment_type_id: 1,
                    payment_type_name: 'Μετρητά'
                };

                // reset checkbox value
                this.paid = false;

                // clone item payments
                let payments = this.item.payments ? [...this.item.payments] : [];

                // add new payment
                payments.push(payment);

                // set data in payments list component
                this.payments.push(payment);

                // call in order to calculate sale's remaining balance
                this.itemsChanged(payments);
            }
        }
    }
</script>